import { useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useAdminGerarPresignedUploadMutation } from '../../api';

const ACEITOS = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
const TAMANHO_MAX_MB = 5;

export type FinalidadeUpload = 'logo' | 'capa' | 'produto' | 'avatar' | 'outros';

interface RetornoUpload {
  enviando: boolean;
  erro: string | null;
  /** Abre o seletor de arquivo (chame no clique da própria imagem). */
  abrirSeletor: () => void;
  /** `<input type="file">` escondido — renderize dentro do alvo clicável. */
  inputArquivo: ReactElement;
}

/**
 * Lógica de upload de imagem (presigned URL + PUT direto no S3), extraída do
 * componente UploadImagem para ser usada quando a PRÓPRIA imagem é o botão de
 * trocar (sem controles separados embaixo).
 */
export function useUploadImagem(
  finalidade: FinalidadeUpload,
  onChange: (urlFinal: string | null) => void,
): RetornoUpload {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [gerar] = useAdminGerarPresignedUploadMutation();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEscolherArquivo(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    setErro(null);
    const arquivo = e.target.files?.[0];
    e.target.value = ''; // reseta pra permitir re-selecionar o mesmo arquivo
    if (!arquivo) return;

    if (!arquivo.type.startsWith('image/')) {
      setErro('Selecione um arquivo de imagem.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX_MB * 1024 * 1024) {
      setErro(`Imagem muito grande — máximo ${TAMANHO_MAX_MB}MB.`);
      return;
    }

    setEnviando(true);
    try {
      const { uploadUrl, urlFinal } = await gerar({
        fileName: arquivo.name,
        fileType: arquivo.type,
        finalidade,
      }).unwrap();

      const resp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': arquivo.type },
        body: arquivo,
      });
      if (!resp.ok) throw new Error(`Falha no upload (HTTP ${resp.status})`);

      onChange(urlFinal);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao enviar imagem');
    } finally {
      setEnviando(false);
    }
  }

  const inputArquivo = (
    <input
      ref={inputRef}
      type="file"
      accept={ACEITOS}
      onChange={aoEscolherArquivo}
      style={{ display: 'none' }}
    />
  );

  return {
    enviando,
    erro,
    abrirSeletor: () => inputRef.current?.click(),
    inputArquivo,
  };
}
